import { Injectable } from '@angular/core';

import { combineLatest, Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

import { CreateTeamError, DeleteTeamError, UpdateTeamError } from '../../errors';
import { Tag, Team, User } from '../../models';

import { AuthenticationService } from '../authentication.service';
import { TagsService } from '../tags.service';
import { TeamsService } from '../teams.service';
import { UsersService } from '../users.service';

import { FirestoreTimestamp } from './firestore-authentication.service';
import { AngularFirestoreCollection, FirestoreService } from './firestore.service';

export interface TeamDTO {
    id: string;
    name: string;
    createdAt: FirestoreTimestamp;
    createdBy: string;
    tagsIds?: string[];
}

@Injectable()
export class FirestoreTeamsService implements TeamsService {
    private get teamsCollection(): AngularFirestoreCollection<TeamDTO> {
        return this.firestoreService.tenantDependentCollection<TeamDTO>(this.authenticationService.user.tenantId, 'teams');
    }

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private tagsService: TagsService,
        private usersService: UsersService
    ) {}

    teams(): Observable<Team[]> {
        const teamsObservable = this.teamsCollection.snapshotChanges().pipe(
            map((result) => {
                return result.map((teamDoc) => {
                    const team = teamDoc.payload.doc.data();
                    team.id = teamDoc.payload.doc.id;
                    return team;
                });
            })
        );

        return teamsObservable.pipe(
            withLatestFrom(combineLatest([this.tagsService.tags(), this.usersService.users()])),
            map(([teamsDTOs, [tags, users]]: [TeamDTO[], [Tag[], User[]]]) => {
                return teamsDTOs.map((teamDTO) => {
                    return this.teamMapper(teamDTO, tags, users);
                });
            })
        );
    }

    async team(teamId: string): Promise<Team | undefined> {
        return new Promise<Team | undefined>((resolve, reject) => {
            const subscription = this.teamsCollection
                .doc(teamId)
                .get()
                .pipe(
                    withLatestFrom(combineLatest([this.tagsService.tags(), this.usersService.users()])),
                    map(([teamDoc, [tags, users]]: [any, [Tag[], User[]]]) => {
                        if (!teamDoc.exists) {
                            return undefined;
                        }

                        const teamDTO = teamDoc.data() as TeamDTO;
                        teamDTO.id = teamDoc.id;

                        return this.teamMapper(teamDTO, tags, users);
                    })
                )
                .subscribe((team: Team) => {
                    resolve(team);
                    subscription.unsubscribe();
                });
        });
    }

    async createTeam(name: string, tagsIds: string[]): Promise<string> {
        const tenantId = this.authenticationService.user.tenantId;
        const createTeam = this.firestoreService.cloudFunction('Users_CreateTeam');
        const result = await createTeam({ name, tagsIds, tenantId });

        if (result && result.data && result.data.code) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new CreateTeamError(
                        'PERMISSION_DENIED',
                        'The authenticated user don\'t have permissions to perform this action.'
                    );
                case 'invalid-argument':
                    throw new CreateTeamError('MISSING_ARGUMENT', 'The property \'name\', \'usersIds\' or \'tenantId\' is missing.');
                case 'already-exists':
                    throw new CreateTeamError('NAME_ALREADY_EXISTS', 'There are already another \'Team\' with this name.');
                default:
                    throw new CreateTeamError('UNKNOWN', 'An unknown error occurred.');
            }
        }

        return result.data.id;
    }

    async deleteTeam(teamId: string): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const deleteTeam = this.firestoreService.cloudFunction('Users_DeleteTeam');

        const result = await deleteTeam({ teamId, tenantId });

        if (result && result.data) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new DeleteTeamError(
                        'PERMISSION_DENIED',
                        'The authenticated user don\'t have permissions to perform this action.'
                    );
                default:
                    throw new DeleteTeamError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async updateTeam(teamId: string, data: { name?: string; tagsIds?: string[]; usersIds?: string[] }): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const updateTeam = this.firestoreService.cloudFunction('Users_UpdateTeam');
        const result = await updateTeam({ teamId, tenantId, ...data });

        if (result && result.data) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new UpdateTeamError(
                        'PERMISSION_DENIED',
                        'The authenticated user don\'t have permissions to perform this action.'
                    );
                case 'invalid-argument':
                    throw new UpdateTeamError('MISSING_ARGUMENT', 'The property \'teamId\', \'name\', \'usersIds\' or \'tenantId\' is missing.');
                case 'already-exists':
                    throw new UpdateTeamError('NAME_ALREADY_EXISTS', 'There are already another \'Team\' with this name.');
                default:
                    throw new UpdateTeamError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    private teamMapper(teamDTO: TeamDTO, tags: Tag[], users: User[]): Team {
        let teamUsers: User[] = [];

        if (teamDTO.tagsIds?.length > 0) {
            teamUsers = users.filter((user: User) => teamDTO.tagsIds.every((tagId: string) => user.tagsIds && user.tagsIds.includes(tagId)));
        } else {
            teamUsers = users.filter((user: User) => !user.tagsIds || user.tagsIds.length === 0);
        }

        return {
            id: teamDTO.id,
            name: teamDTO.name,
            createdAt: teamDTO.createdAt.toDate(),
            createdBy: users.find((user) => user.id === teamDTO.createdBy),
            tags: tags.filter((tag: Tag) => teamDTO.tagsIds && teamDTO.tagsIds.includes(tag.id)),
            users: teamUsers.sort((a, b) => (a.name > b.name ? 1 : -1)),
        };
    }
}
